import React, {
  useState,
  useEffect,
  useMemo,
  use,
  useCallback,
  useRef,
  act,
} from "react";
import * as signalR from "@microsoft/signalr";
import { useMsal } from "@azure/msal-react";
import {
  Batch,
  deleteBatch,
  getBatches,
  getbatch,
  getReconciliation,
  ReconciliationDetail,
  updateBatchDate,
  updateBatchColor,
  markBatchComplete,
  BatchProgress,
  getBatchProgress,
  getUnfinishedBatches,
  retryPendingPages,
} from "../services/batchApiService";
import {
  TableAction,
  TanStackDynamicTable,
} from "../components/TanStackDynamicTable";
import {
  ColumnDef,
  ColumnFiltering,
  ColumnFiltersState,
  SortingState,
} from "@tanstack/react-table";
import {
  CoverSheetDetail,
  deleteCoverSheetDetails,
  getCoverSheet,
  upsertCoverSheetDetails,
} from "../services/coversheetApiService";
import {
  Calendar,
  CheckCircle,
  Database,
  Edit,
  ExternalLink,
  Notebook,
  PlusIcon,
  Save,
  Scale,
  SearchIcon,
  Send,
  Trash,
  Trash2,
  UserPen,
  Circle,
  Loader2,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { CustomSelect } from "../components/CustomSelect";
import { getTicketMetadata, TicketMetadata } from "../services/scansApiService";
import { getTickets, getTicketIds, pushTicketsBatch } from "../services/ticketApiService";
import { useNavigate, useLocation } from "react-router-dom";
import {
  addNote,
  deleteNote,
  getNotes,
  Note,
  updateNote,
} from "../services/apiService";
import { NotesPane } from "../components/NotesPane";
import { ProDatePicker } from "../components/ProDatePicker";
import { useUser } from "../contexts/UserContext";
import {
  BatchColorSetting,
  getBatchColorSettings,
} from "../services/batchColorApiService";
import { ColorTagPicker, ColorOption } from "../components/ColorTagPicker";
import { DynamicIcon } from "../components/IconPicker";
import {
  BatchPreset,
  BatchPresetModal,
  loadPresets,
} from "../components/BatchPresetModal";
import { Filter } from "lucide-react";

const BatchPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const { isAdmin } = useUser();
  const navigate = useNavigate();
  const location = useLocation();

  const [batchData, setBatchData] = useState<Batch[]>([]);
  const [batchCount, setBatchCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scenario, setScenario] = useState(1);
  const pageSize = 50;

  const [reconcileData, setReconcileData] = useState<
    (ReconciliationDetail & { id: string })[]
  >([]);
  const [isReconcileLoading, setIsReconcileLoading] = useState(false);
  const [coverSheetData, setCoverSheetData] = useState<CoverSheetDetail[]>([]);
  const [isCoverSheetLoading, setIsCoverSheetLoading] = useState(false);
  const [metadata, setMetadata] = useState<TicketMetadata | null>(null);

  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem("batch_searchTerm") || "");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | null>(() => {
    const saved = localStorage.getItem("batch_statusFilter");
    return saved !== null ? (saved === "null" ? null : saved) : "Active";
  });
  const [showDateFilters, setShowDateFilters] = useState(() => localStorage.getItem("batch_showDateFilters") === "true");
  const [startDate, setStartDate] = useState<string | null>(() => localStorage.getItem("batch_startDate"));
  const [endDate, setEndDate] = useState<string | null>(() => localStorage.getItem("batch_endDate"));
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);

  const [showDateModal, setShowDateModal] = useState(false);
  const [newDate, setNewDate] = useState("");

  const [colorDropdownBatchId, setColorDropdownBatchId] = useState<string | null>(null);
  const [colorFilter, setColorFilter] = useState<string | null>(null);
  const [issueFilter, setIssueFilter] = useState<string | null>(null);
  const [colorSettings, setColorSettings] = useState<BatchColorSetting[]>([]);

  const [presets, setPresets] = useState<BatchPreset[]>(() => loadPresets());
  const [selectedPresetId, setSelectedPresetId] = useState<string | null>(
    () => localStorage.getItem("batch_selectedPresetId") || null,
  );
  const [presetModalOpen, setPresetModalOpen] = useState(false);

  useEffect(() => {
    if (selectedPresetId) localStorage.setItem("batch_selectedPresetId", selectedPresetId);
    else localStorage.removeItem("batch_selectedPresetId");
  }, [selectedPresetId]);

  const selectedPreset = presets.find((p) => p.id === selectedPresetId) ?? null;

  useEffect(() => {
    if (accounts.length === 0) return;
    getBatchColorSettings(instance, accounts[0])
      .then(setColorSettings)
      .catch((e) => console.error("Failed to load color settings", e));
  }, [instance, accounts]);

  const visibleColors = useMemo<ColorOption[]>(() => {
    return colorSettings
      .filter((s) => s.isEnabled)
      .map((s) => ({
        value: s.colorValue,
        hexColor: s.hexColor || "#94a3b8",
        icon: s.icon,
        label: s.label || s.colorValue,
      }));
  }, [colorSettings]);

  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [notesPinned, setNotesPinned] = useState(false);
  const [notesBatchId, setNotesBatchId] = useState<string | null>(null);
  const [notes, setNotes] = useState<Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText] = useState("");
  const [editingNoteId, setEditingNoteId] = useState<string | number | null>(
    null,
  );
  const [editingNoteText, setEditingNoteText] = useState("");

  // Live processing progress: sidebar badges for unfinished batches + "Processed X/Y" for the selected one
  const [progressByBatch, setProgressByBatch] = useState<Record<string, BatchProgress>>({});
  const [selectedProgress, setSelectedProgress] = useState<BatchProgress | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const selectedBatchIdRef = useRef<string | null>(null);
  const onSelectedBatchFinishedRef = useRef<() => void>(() => {});

  const fetchBatchData = async (page: number) => {
    if (accounts.length === 0) return;
    try {
      setLoading(true);
      const result = await getBatches(
        instance,
        accounts[0],
        page,
        searchTerm,
        statusFilter,
        colorFilter,
        startDate,
        endDate,
        selectedPreset?.branches ?? null,
        selectedPreset?.warehouses ?? null,
        issueFilter,
      );
      setBatchData(result.data);
      setBatchCount(result.totalCount);
    } catch (error) {
      console.error("Failed to load batches", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshBatch = async (batchId: string) => {
    if (accounts.length === 0) return;
    try {
      const updated = await getbatch(instance, accounts[0], batchId);
      setBatchData((prev) =>
        prev.map((b) => (b.batchId === batchId ? { ...b, ...updated } : b))
      );
      if (selectedBatch?.batchId === batchId) {
        setSelectedBatch((prev) => prev ? { ...prev, ...updated } : prev);
      }
    } catch (err) {
      console.error("Failed to refresh batch", err);
    }
  };

  const fetchCoverSheetData = async (batchId: string, scenario: number) => {
    if (accounts.length === 0) return;
    setIsCoverSheetLoading(true);
    try {
      const result = await getCoverSheet(
        instance,
        accounts[0],
        batchId,
        scenario,
      );
      setCoverSheetData(result.data);
    } catch (error) {
      console.error("Failed to load cover sheet data", error);
    } finally {
      setIsCoverSheetLoading(false);
    }
  };

  const fetchMetadata = async () => {
    if (accounts.length === 0) return;
    try {
      const result = await getTicketMetadata();
      setMetadata(result);
    } catch (error) {
      console.error("Failed to load metadata", error);
    }
  };

  const fetchReconciliationData = async () => {
    if (!selectedBatch || !accounts[0]) return;
    setIsReconcileLoading(true);
    try {
      const result = await getReconciliation(
        instance,
        accounts[0],
        selectedBatch.batchId,
      );

      const mappedData = result.data.map((row: ReconciliationDetail) => ({
        ...row,
        id: `${row.inventoryId}-${row.vendorId}-${row.freightVendorId}`,
      }));

      setReconcileData(mappedData);

      const issueCount = mappedData.filter((r) => r.status !== "Match").length;
      const batchId = selectedBatch.batchId;
      setBatchData((prev) =>
        prev.map((b) => (b.batchId === batchId ? { ...b, issueCount } : b))
      );
      setSelectedBatch((prev) => (prev ? { ...prev, issueCount } : prev));
    } catch (e) {
      console.error("Reconciliation fetch error:", e);
    } finally {
      setIsReconcileLoading(false);
    }
  };

  useEffect(() => { localStorage.setItem("batch_searchTerm", searchTerm); }, [searchTerm]);
  useEffect(() => { localStorage.setItem("batch_statusFilter", statusFilter === null ? "null" : statusFilter); }, [statusFilter]);
  useEffect(() => { localStorage.setItem("batch_showDateFilters", String(showDateFilters)); }, [showDateFilters]);
  useEffect(() => { startDate ? localStorage.setItem("batch_startDate", startDate) : localStorage.removeItem("batch_startDate"); }, [startDate]);
  useEffect(() => { endDate ? localStorage.setItem("batch_endDate", endDate) : localStorage.removeItem("batch_endDate"); }, [endDate]);

  useEffect(() => {
    fetchMetadata();
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      fetchCoverSheetData(selectedBatch.batchId, scenario);
    }
  }, [selectedBatch?.batchId, scenario]);

  useEffect(() => {
    fetchBatchData(currentPage);
  }, [currentPage, statusFilter, colorFilter, issueFilter, startDate, endDate, selectedPresetId]);

  // Auto-select batch when returning from detail page
  useEffect(() => {
    const navState = location.state as { selectedBatchId?: string } | null;
    if (navState?.selectedBatchId && batchData.length > 0) {
      const match = batchData.find(b => b.batchId === navState.selectedBatchId);
      if (match) setSelectedBatch(match);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [batchData, location.state]);

  useEffect(() => {
    fetchReconciliationData();
  }, [selectedBatch?.batchId, accounts, instance]);

  // Newly finished pages change the scanned counts, so refresh reconciliation once a run ends
  onSelectedBatchFinishedRef.current = () => {
    fetchReconciliationData();
  };

  useEffect(() => {
    selectedBatchIdRef.current = selectedBatch?.batchId ?? null;
    setSelectedProgress(null);
    if (!selectedBatch || accounts.length === 0) return;
    getBatchProgress(instance, accounts[0], selectedBatch.batchId)
      .then((p) => {
        if (selectedBatchIdRef.current === p.batchId) setSelectedProgress(p);
      })
      .catch((e) => console.error("Failed to load batch progress", e));
  }, [selectedBatch?.batchId, accounts, instance]);

  useEffect(() => {
    if (accounts.length === 0) return;

    const loadUnfinished = () =>
      getUnfinishedBatches(instance, accounts[0])
        .then((list) => setProgressByBatch(Object.fromEntries(list.map((p) => [p.batchId, p]))))
        .catch((e) => console.error("Failed to load processing batches", e));

    const conn = new signalR.HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_API_BASE_URL}/hubs/scans`)
      .withAutomaticReconnect([0, 2000, 5000, 10000])
      .build();

    conn.on("BatchProgress", (p: BatchProgress) => {
      setProgressByBatch((prev) => {
        const next = { ...prev };
        if (p.pending === 0 && !p.isProcessing) delete next[p.batchId];
        else next[p.batchId] = p;
        return next;
      });
      if (selectedBatchIdRef.current === p.batchId) {
        setSelectedProgress((prev) => {
          if (prev?.isProcessing && !p.isProcessing) onSelectedBatchFinishedRef.current();
          return p;
        });
      }
    });

    conn.onreconnected(() => {
      conn.invoke("JoinBatchProgress");
      loadUnfinished();
    });

    loadUnfinished();
    conn
      .start()
      .then(() => conn.invoke("JoinBatchProgress"))
      .catch((err) => console.error("SignalR Connection Error: ", err));

    return () => {
      conn.stop();
    };
  }, [accounts, instance]);

  const handleRetryPending = async () => {
    if (!selectedBatch) return;
    setIsRetrying(true);
    try {
      const p = await retryPendingPages(instance, accounts[0], selectedBatch.batchId);
      setSelectedProgress(p);
    } catch (error) {
      console.error("Error retrying stuck pages:", error);
      alert("Failed to retry stuck pages. It may already be processing.");
    } finally {
      setIsRetrying(false);
    }
  };

  // const filteredBatches = useMemo(() => {
  //   return batchData.filter(
  //     (b) =>
  //       b.batchId.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //       b.warehouse.toLowerCase().includes(searchTerm.toLowerCase()),
  //   );
  // }, [batchData, searchTerm]);

  const totalPages = Math.ceil(batchCount / pageSize);

  useEffect(() => {
    if (isNotesOpen && notesBatchId && accounts[0]) {
      setNotesLoading(true);
      getNotes(instance, accounts[0], notesBatchId)
        .then((data) => setNotes(data || []))
        .catch(console.error)
        .finally(() => setNotesLoading(false));
    }
  }, [isNotesOpen, notesBatchId, instance, accounts]);

  const handleCreateNote = async () => {
    if (!newNoteText.trim() || !notesBatchId || !accounts[0]) return;

    const newNote: Note = {
      id: 0,
      batchId: notesBatchId,
      author: accounts[0].name || "Unknown",
      createdDateTime: new Date(),
      note: newNoteText,
    };

    try {
      await addNote(instance, accounts[0], newNote);
      setNewNoteText("");
      const data = await getNotes(instance, accounts[0], notesBatchId);
      setNotes(data || []);
    } catch (err) {
      console.error(err);
      alert("Failed to add note");
    }
  };

  const handleUpdateNote = async (note: Note) => {
    if (!editingNoteText.trim() || !accounts[0]) return;
    try {
      await updateNote(instance, accounts[0], {
        ...note,
        note: editingNoteText,
      });
      setEditingNoteId(null);
      setEditingNoteText("");
      if (notesBatchId) {
        const data = await getNotes(
          instance,
          accounts[0],
          notesBatchId,
        );
        setNotes(data || []);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to update note");
    }
  };

  const handleDeleteNote = async (note: Note) => {
    if (!accounts[0] || !window.confirm("Delete this note?")) return;
    try {
      await deleteNote(instance, accounts[0], note.id);
      setNotes((prev) => prev.filter((n) => n.id !== note.id));
    } catch (err) {
      console.error(err);
      alert("Failed to delete note");
    }
  };

  const reconciliationColumns = useMemo<ColumnDef<ReconciliationDetail>[]>(
    () => [
      {
        id: "inventoryId",
        header: "Material",
        accessorKey: "inventoryId",
        meta: { type: "text" },
      },
      {
        id: "vendorId",
        header: "Supplier",
        accessorKey: "vendorId",
        meta: { type: "text" },
      },
      {
        id: "freightVendorId",
        header: "Hauler",
        accessorKey: "freightVendorId",
        meta: { type: "text" },
      },
      {
        id: "receivedQtyCover",
        header: "Cover Qty",
        accessorKey: "receivedQtyCover",
        meta: { type: "number" },
      },
      {
        id: "receivedQtyScanned",
        header: "Scanned Qty",
        accessorKey: "receivedQtyScanned",
        meta: { type: "number" },
      },
      {
        id: "receivedQtyVariance",
        header: "Qty Var",
        accessorKey: "receivedQtyVariance",
        meta: { type: "number" },
        cell: ({ getValue }) => {
          const val = getValue() as number;
          return (
            <span
              className={`font-bold ${val !== 0 ? "text-red-500" : "text-emerald-500"}`}
            >
              {val.toFixed(2)}
            </span>
          );
        },
      },
      {
        id: "countVariance",
        header: "Count Var",
        accessorKey: "countVariance",
        meta: { type: "number" },
        cell: ({ getValue }) => {
          const val = getValue() as number;
          return (
            <span
              className={`font-bold ${val !== 0 ? "text-red-500" : "text-emerald-500"}`}
            >
              {val}
            </span>
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "status", // Note: Ensure backend JSON matches casing
        meta: { type: "text" },
        cell: ({ getValue }) => {
          const status = getValue() as string;
          const colorMap: Record<string, string> = {
            Match:
              "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400",
            "Missing Cover Sheet":
              "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
            "Missing Tickets":
              "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
            Mismatch:
              "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400",
          };
          return (
            <span
              className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${colorMap[status] || "bg-slate-100"}`}
            >
              {status}
            </span>
          );
        },
      },
    ],
    [],
  );

  const uomOptions = ["TN", "GA", "EA"].map((u) => ({ label: u, value: u }));

  const handleEdit = async () => {
    if (!selectedBatch) return;
    const batchFilter = `BatchID == "${selectedBatch.batchId}" && Status != "Deleted"`;
    const ids = await getTicketIds(instance, accounts[0], batchFilter);
    if (ids.length === 0) {
      alert("No tickets found for this batch.");
      return;
    }
    const queueKey = `queue_${Date.now()}`;
    sessionStorage.setItem(queueKey, JSON.stringify({ queue: ids, index: 0, returnPath: "/batch", batchId: selectedBatch.batchId }));
    navigate(`/ticket/${ids[0]}?queueKey=${queueKey}`);
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Are you sure you want to delete this batch?\nIt will soft delete all tickets and cover sheets will no longer be accessible.")) return;
    await deleteBatch(instance, accounts[0], selectedBatch.batchId);
    setBatchData((oldData) =>
      oldData.filter((row) => row.batchId !== selectedBatch!.batchId),
    );
    setSelectedBatch(null);

    alert("Successfully deleted tickets, batch, and coversheets.");
  };

  const handlePushBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm("Are you sure you want to push this batch?")) return;
    try {
      const result = await getTickets(instance, accounts[0], `BatchID == "${selectedBatch.batchId}"`);
      const ids = result.data?.map((t) => Number(t.id)) || [];
      const res = await pushTicketsBatch(instance, accounts[0], ids);
      const results = res.results || [];
      const successes = results.filter((r: any) => r.success);
      const failures = results.filter((r: any) => !r.success);
      let alertMsg = `Push Results:\n✅ ${successes.length} Successful\n❌ ${failures.length} Failed`;

      if (failures.length > 0) {
        alertMsg +=
          `\n\nErrors:\n` +
          failures
            .map((f: any) => `Ticket ${f.ticketId}: ${f.error}`)
            .join("\n");
      }

      window.alert(alertMsg);
      await refreshBatch(selectedBatch.batchId);
    } catch (error) {
      console.error("Error pushing batch:", error);
      alert("Failed to push batch. Check the console for details.");
    }
  }

  const handleDateChange = async () => {
    if (!selectedBatch || !newDate) return;
    try {
      await updateBatchDate(instance, accounts[0], selectedBatch.batchId, newDate);
      setShowDateModal(false);
      await fetchBatchData(currentPage);
      setSelectedBatch({ ...selectedBatch, orderDate: newDate });
    } catch (error) {
      console.error("Error updating batch date:", error);
      alert("Failed to update batch date.");
    }
  };

  const reconciliationActions: TableAction[] = useMemo(() => {
    const actions: TableAction[] = [
      {
        label: "Review Tickets",
        icon: <Edit size={16} className="text-primary" />,
        onClick: handleEdit,
      },
      {
        label: "View in Pending",
        icon: <ExternalLink size={16} className="text-blue-500" />,
        onClick: () => {
          if (selectedBatch) {
            navigate(`/pending?batchId.equals=${selectedBatch.batchId}`);
          }
        },
      },
      {
        label: "Push Batch",
        icon: <Send size={16} className="text-emerald-500" />,
        onClick: handlePushBatch,
      },
      {
        label: "Mark Complete",
        icon: <CheckCircle size={16} className="text-emerald-500" />,
        onClick: async () => {
          if (!selectedBatch) return;
          if (!window.confirm("Mark this batch as completed?")) return;
          try {
            await markBatchComplete(instance, accounts[0], selectedBatch.batchId);
            await refreshBatch(selectedBatch.batchId);
          } catch (error) {
            console.error("Error marking batch complete:", error);
            alert("Failed to mark batch as completed.");
          }
        },
      },
      {
        label: "Delete Batch",
        icon: <Trash2 size={16} className="text-red-500" />,
        onClick: handleDeleteBatch,
      },
      {
        label: "Change Date",
        icon: <Calendar size={16} className="text-primary" />,
        onClick: () => {
          if (selectedBatch) {
            setNewDate(selectedBatch.orderDate);
            setShowDateModal(true);
          }
        },
      },
    ];

    if (isAdmin) return actions;
    else return actions.filter((a) => a.label === "Review Tickets" || a.label === "View in Pending" || a.label === "Push Batch" || a.label === "Mark Complete" || a.label === "Change Date");
  }, [handleEdit, isAdmin, selectedBatch]);

  const handleDelete = async (selectedRows: any[]) => {
    const ids = selectedRows.map((r) => r.id);
    if (
      window.confirm(
        `Are you sure you want to delete ${ids.length} selected row(s)?`,
      )
    ) {
      try {
        await deleteCoverSheetDetails(instance, accounts[0], ids);
        setCoverSheetData((oldData) =>
          oldData.filter((row) => !ids.includes(row.id)),
        );
        await fetchReconciliationData();
      } catch (error) {
        console.error("Failed to delete cover sheet details", error);
      }
    }
  };

  const handleSave = useCallback(async () => {
    setIsCoverSheetLoading(true);
    try {
      await upsertCoverSheetDetails(instance, accounts[0], coverSheetData);
      await fetchCoverSheetData(selectedBatch!.batchId, scenario);
      await fetchReconciliationData();
      await refreshBatch(selectedBatch!.batchId);
    } catch (error) {
      console.error("Failed to save cover sheet details", error);
    } finally {
      setIsCoverSheetLoading(false);
    }
  }, [instance, accounts, coverSheetData, fetchReconciliationData]);

  const coverSheetActions: TableAction[] = useMemo(() => {
    const actions: TableAction[] = [
      {
        label: "Accounting",
        icon: (
          <UserPen
            size={16}
            className={scenario === 1 ? "text-yellow-500" : "text-gray-400"}
          />
        ),
        onClick: () => {
          setScenario(1);
        },
      },
      {
        label: "Plant",
        icon: (
          <Database
            size={16}
            className={scenario === 0 ? "text-yellow-500" : "text-gray-400"}
          />
        ),
        onClick: () => {
          setScenario(0);
        },
      },
      {
        label: "Add Row",
        icon: <PlusIcon size={16} className="text-emerald-500" />,
        onClick: () => {
          setCoverSheetData((oldData) => [
            {
              id: 0,
              warehouse: selectedBatch?.warehouse || "",
              branch: selectedBatch?.branch || "",
              inventoryID: "",
              vendorID: "",
              freightVendorID: "",
              loads: 0,
              receivedQty: 0,
              uom: "TN",
              scenario: 1,
              orderDate:
                selectedBatch?.orderDate ||
                new Date().toISOString().split("T")[0],
              batchId: selectedBatch?.batchId || "",
              createdTimestamp: new Date().toISOString(),
            },
            ...oldData,
          ]);
        },
        disabled: scenario === 0,
      },
      {
        label: "Delete",
        icon: <Trash2 size={16} className="text-red-500" />,
        onClick: handleDelete,

        disabled: scenario === 0,
      },
      {
        label: "Save",
        icon: <Save size={16} className="text-primary" />,
        onClick: handleSave,
        disabled: scenario === 0,
      },
    ];

    if (scenario === 0) {
      return actions.filter(
        (action) => action.label === "Accounting" || action.label === "Plant",
      );
    }

    return actions;
  }, [scenario, selectedBatch, coverSheetData, handleSave, handleDelete]);

  const handleCellEdit = (rowIndex: number, columnId: string, value: any) => {
    setCoverSheetData((oldData) =>
      oldData.map((row, index) => {
        if (index === rowIndex) {
          return {
            ...row,
            [columnId]: value,
          };
        }
        return row;
      }),
    );
  };

  const coverSheetColumns = useMemo<ColumnDef<CoverSheetDetail>[]>(() => {
    const cols: ColumnDef<CoverSheetDetail>[] = [
      {
        id: "select",
        header: ({ table }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={table.getIsAllPageRowsSelected()}
              onChange={table.getToggleAllPageRowsSelectedHandler()}
            />
          </div>
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
              checked={row.getIsSelected()}
              disabled={!row.getCanSelect()}
              onChange={row.getToggleSelectedHandler()}
            />
          </div>
        ),
        size: 20,
      },
      {
        id: "inventoryID",
        header: "Material",
        accessorKey: "inventoryID",
        meta: { type: "text" },
        cell: ({ getValue, row }) => {
          const val = getValue() as string;
          if (scenario === 1) {
            return (
              <CustomSelect
                value={val}
                options={
                  metadata?.coverSheetInventoryItems.map((item) => ({
                    label: `${item.inventoryId} - ${item.inventoryDesc}`,
                    value: item.inventoryId,
                  })) || []
                }
                onChange={(newVal) =>
                  handleCellEdit(row.index, "inventoryID", String(newVal))
                }
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
      {
        id: "vendorID",
        header: "Supplier",
        accessorKey: "vendorID",
        meta: { type: "text" },
        cell: ({ getValue, row }) => {
          const val = getValue() as number;
          if (scenario === 1) {
            return (
              <CustomSelect
                value={val}
                options={
                  metadata?.vendors.map((item) => ({
                    label: `${item.vendorId} - ${item.vendorIddesc}`,
                    value: item.vendorId,
                  })) || []
                }
                onChange={(newVal) =>
                  handleCellEdit(row.index, "vendorID", newVal)
                }
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
      {
        id: "freightVendorID",
        header: "Hauler",
        accessorKey: "freightVendorID",
        meta: { type: "text" },
        cell: ({ getValue, row }) => {
          const val = getValue() as number;
          if (scenario === 1) {
            return (
              <CustomSelect
                value={val}
                options={
                  metadata?.freightVendors.map((item) => ({
                    label: `${item.freightVendorId} - ${item.freightVendorIddesc}`,
                    value: item.freightVendorId,
                  })) || []
                }
                onChange={(newVal) =>
                  handleCellEdit(row.index, "freightVendorID", newVal)
                }
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
      {
        id: "loads",
        header: "Loads",
        accessorKey: "loads",
        meta: { type: "number" },
        cell: ({ getValue, row }) => {
          const val = getValue() as number;
          if (scenario === 1) {
            return (
              <input
                type="number"
                value={val}
                onChange={(e) =>
                  handleCellEdit(row.index, "loads", Number(e.target.value))
                }
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm transition-all outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
      {
        id: "receivedQty",
        header: "Qty",
        accessorKey: "receivedQty",
        meta: { type: "number" },
        cell: ({ getValue, row }) => {
          const val = getValue() as number;
          if (scenario === 1) {
            return (
              <input
                type="number"
                value={val}
                onChange={(e) =>
                  handleCellEdit(
                    row.index,
                    "receivedQty",
                    Number(e.target.value),
                  )
                }
                className="w-full h-10 px-3 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-sm transition-all outline-none focus:ring-2 focus:ring-primary/50 text-slate-900 dark:text-white placeholder:text-slate-400"
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
      {
        id: "uom",
        header: "UOM",
        accessorKey: "uom",
        meta: { type: "text" },
        cell: ({ getValue, row }) => {
          const val = getValue() as number;
          if (scenario === 1) {
            return (
              <CustomSelect
                value={val}
                options={uomOptions}
                onChange={(newVal) => handleCellEdit(row.index, "uom", newVal)}
              />
            );
          }
          return <span className="px-3 py-2">{val}</span>;
        },
      },
    ];

    if (scenario === 0) {
      return cols.filter((column) => column.id !== "select");
    }
    return cols;
  }, [metadata, scenario]);

  return (
    <div className="flex flex-col h-screen w-full bg-gray-100 dark:bg-slate-950 overflow-hidden font-sans transition-colors">
      {/* --- TOP FILTER HEADER --- */}
      <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 shadow-sm px-4 py-2 flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="flex gap-2 items-center px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          <input
            type="text"
            placeholder="Search..."
            className="w-56 px-2.5 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-gray-900 dark:text-slate-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") fetchBatchData(currentPage); }}
          />
          <button
            onClick={() => fetchBatchData(currentPage)}
            className="bg-primary px-3 py-1.5 rounded-lg text-white hover:bg-primary/90"
          >
            <SearchIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Preset */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          <div className="w-48">
            <CustomSelect
              value={selectedPresetId}
              options={[
                { label: "All (no preset)", value: "" },
                ...presets.map((p) => ({ label: p.name, value: p.id })),
              ]}
              onChange={(val) => {
                setSelectedPresetId(val === "" ? null : val);
                setCurrentPage(1);
              }}
              placeholder="All (no preset)"
            />
          </div>
          <button
            onClick={() => setPresetModalOpen(true)}
            className="p-1.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
            title="Manage Presets"
          >
            <Filter size={16} />
          </button>
        </div>

        {/* Status Chips */}
        <div className="flex gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          {([
            { label: "Active", value: "Active" },
            { label: "Completed", value: "Completed" },
            { label: "All", value: null },
          ] as const).map((chip) => (
            <button
              key={chip.label}
              onClick={() => { setStatusFilter(chip.value); setCurrentPage(1); }}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                statusFilter === chip.value
                  ? "bg-primary text-white shadow-sm"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Issue Filter */}
        <div className="flex gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          {([
            { label: "Has Issues", value: "HasIssues" },
            { label: "No Issues", value: "NoIssues" },
            { label: "New", value: "Unreconciled" },
            { label: "All", value: null },
          ] as const).map((chip) => (
            <button
              key={chip.label}
              onClick={() => { setIssueFilter(chip.value); setCurrentPage(1); }}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                issueFilter === chip.value
                  ? "bg-primary text-white shadow-sm"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Color Filter */}
        <div className="flex gap-1.5 items-center px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          {visibleColors.map((color) => (
            <button
              key={color.value}
              onClick={() => { setColorFilter(colorFilter === color.value ? null : color.value); setCurrentPage(1); }}
              className={`w-5 h-5 rounded-full transition-transform hover:scale-110 flex items-center justify-center ${
                colorFilter === color.value ? "ring-2 ring-offset-1 scale-110" : "opacity-60 hover:opacity-100"
              }`}
              style={{ backgroundColor: color.hexColor }}
              title={color.label}
            >
              {color.icon && <DynamicIcon name={color.icon} size={10} className="text-white" />}
            </button>
          ))}
          {colorFilter && (
            <button
              onClick={() => { setColorFilter(null); setCurrentPage(1); }}
              className="text-[10px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 ml-1"
            >
              clear
            </button>
          )}
        </div>

        {/* Date Filters */}
        <div className="flex gap-2 items-center px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-700">
          <button
            onClick={() => {
              const next = !showDateFilters;
              setShowDateFilters(next);
              if (!next) { setStartDate(null); setEndDate(null); setCurrentPage(1); }
            }}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${showDateFilters ? "bg-primary text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
          >
            <Calendar className="w-3 h-3" />
            Date Range
          </button>
          {showDateFilters && (
            <>
              <div className="w-36">
                <ProDatePicker
                  value={startDate}
                  onChange={(val) => { setStartDate(val || null); setCurrentPage(1); }}
                />
              </div>
              <span className="text-xs text-slate-400">to</span>
              <div className="w-36">
                <ProDatePicker
                  value={endDate}
                  onChange={(val) => { setEndDate(val || null); setCurrentPage(1); }}
                />
              </div>
            </>
          )}
        </div>
      </header>

      {/* --- BODY: SIDEBAR + MAIN --- */}
      <div className="flex flex-1 overflow-hidden">
        {/* --- SIDEBAR ASIDE --- */}
        <aside className="w-50 bg-white dark:bg-slate-900 border-r border-gray-200 dark:border-slate-800 flex flex-col shadow-lg">
          {/* Scrollable Batch List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50 dark:bg-slate-900/50">
          {loading ? (
            <div className="flex justify-center p-10 text-gray-400 italic">
              Loading...
            </div>
          ) : (
            batchData.map((batch) => (
              <div
                key={batch.batchId}
                onClick={() => setSelectedBatch(batch)}
                className={`p-4 rounded-lg border transition-all cursor-pointer shadow-sm relative
            ${
              selectedBatch?.batchId === batch.batchId
                ? "bg-blue-50 dark:bg-blue-900/30 border-blue-400 ring-1 ring-blue-400"
                : "bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 text-gray-700 dark:text-slate-300 hover:border-blue-300"
            }`}
              >
                <div className="flex flex-col gap-3 items-start mb-1">
                  <span className="text-lg font-bold text-gray-700 dark:text-slate-100">
                    {batch.batchId}
                  </span>

                  <span className="text-lg font-bold text-gray-700 dark:text-slate-100">
                    {batch.branch} | {batch.warehouse}
                  </span>
                  <span
                    className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${
                      batch.issueCount === -1
                        ? "bg-yellow-100 text-yellow-700"
                        : batch.issueCount === 0
                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                          : "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400"
                    }`}
                  >
                    {batch.status}
                  </span>
                  {progressByBatch[batch.batchId]?.isProcessing ? (
                    <span className="flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                      <Loader2 size={10} className="animate-spin" /> Processing
                    </span>
                  ) : progressByBatch[batch.batchId]?.pending > 0 ? (
                    <span className="flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400">
                      <AlertTriangle size={10} /> {progressByBatch[batch.batchId].pending} stuck
                    </span>
                  ) : null}
                </div>
                {/* Color Tag Button */}
                <div className="absolute bottom-2 right-10" onClick={(e) => e.stopPropagation()}>
                  <ColorTagPicker
                    value={batch.colorTag}
                    colors={visibleColors}
                    onChange={async (newColor) => {
                      try {
                        await updateBatchColor(instance, accounts[0], batch.batchId, newColor);
                        const label =
                          visibleColors.find((c) => c.value === newColor)?.label ?? "None";
                        await addNote(instance, accounts[0], {
                          id: 0,
                          batchId: batch.batchId,
                          author: accounts[0].name || "Unknown",
                          createdDateTime: new Date(),
                          note: `Set Status to ${label}`,
                        });
                        setBatchData((prev) =>
                          prev.map((b) =>
                            b.batchId === batch.batchId
                              ? { ...b, colorTag: newColor, noteCount: (b.noteCount ?? 0) + 1 }
                              : b
                          )
                        );
                        if (selectedBatch?.batchId === batch.batchId) {
                          setSelectedBatch((prev) => prev ? { ...prev, colorTag: newColor } : prev);
                        }
                        if (notesBatchId === batch.batchId) {
                          const data = await getNotes(instance, accounts[0], batch.batchId);
                          setNotes(data || []);
                        }
                      } catch (err) {
                        console.error("Error updating color tag:", err);
                      }
                    }}
                  />
                </div>

                {/* Notes Button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (notesPinned && notesBatchId === batch.batchId) {
                      setNotesPinned(false);
                      setIsNotesOpen(false);
                      setNotesBatchId(null);
                    } else {
                      setNotesBatchId(batch.batchId);
                      setIsNotesOpen(true);
                      setNotesPinned(true);
                    }
                  }}
                  onMouseEnter={() => {
                    if (!notesPinned) {
                      setNotesBatchId(batch.batchId);
                      setIsNotesOpen(true);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!notesPinned) {
                      setIsNotesOpen(false);
                      setNotesBatchId(null);
                    }
                  }}
                  className={`absolute bottom-2 right-2 p-1.5 rounded-md transition-all ${
                    notesPinned && notesBatchId === batch.batchId
                      ? "bg-primary text-white shadow-sm"
                      : "text-slate-400 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-700"
                  }`}
                  title="Notes"
                >
                  <Notebook size={20} />
                  {batch.noteCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-primary border-2 border-white dark:border-slate-900 rounded-full" />
                  )}
                </button>
              </div>
            ))
          )}
        </div>

        {/* Pagination Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between text-sm transition-colors">
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
            className="px-3 py-1 border border-gray-300 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Prev
          </button>
          <span className="text-gray-600 dark:text-slate-400 font-medium">
            {currentPage} / {totalPages || 1}
          </span>
          <button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
            className="px-3 py-1 border border-gray-300 dark:border-slate-700 rounded bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>
      </aside>

      {/* --- MAIN CONTENT --- */}
      <main className="flex-1 overflow-hidden bg-white dark:bg-slate-900 flex flex-col relative">
        {selectedBatch?.batchId ? (
          <>
            <div className="p-6 border-b dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900">
              <h1 className="text-2xl font-extrabold flex gap-6">
                <span className="text-primary">{selectedBatch?.batchId}</span>
                <span className="text-slate-500 dark:text-slate-400 font-normal">
                  {selectedBatch.orderDate}
                </span>
                <span className="text-slate-500 dark:text-slate-400 font-normal">
                  {" "}
                  {selectedBatch?.branch} | {selectedBatch?.warehouse}
                </span>
              </h1>
              {selectedProgress && selectedProgress.total > 0 && (
                <div className="flex items-center gap-3">
                  <span
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                      selectedProgress.isProcessing
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300"
                        : selectedProgress.pending > 0
                          ? "bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-400"
                          : "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                    }`}
                    title={
                      selectedProgress.pending > 0 && !selectedProgress.isProcessing
                        ? "These pages failed to process and are not tickets yet"
                        : undefined
                    }
                  >
                    {selectedProgress.isProcessing ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : selectedProgress.pending > 0 ? (
                      <AlertTriangle size={12} />
                    ) : (
                      <CheckCircle size={12} />
                    )}
                    Processed {selectedProgress.processed}/{selectedProgress.total}
                    {!selectedProgress.isProcessing && selectedProgress.pending > 0 && (
                      <> · {selectedProgress.pending} stuck</>
                    )}
                  </span>
                  {!selectedProgress.isProcessing && selectedProgress.pending > 0 && (
                    <button
                      onClick={handleRetryPending}
                      disabled={isRetrying}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50"
                    >
                      <RotateCcw size={12} className={isRetrying ? "animate-spin" : ""} />
                      Retry stuck pages
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex-1 overflow-auto p-6 gap-6 flex flex-col">
              <TanStackDynamicTable
                data={reconcileData}
                columns={reconciliationColumns}
                rowCount={reconcileData.length}
                tableName="reconcile_view"
                // If your table requires onStateChange, provide a simple no-op or local state
                onStateChange={() => {}}
                showPagination={false}
                //   isLoading={isReconcileLoading}
                // We can leave actions empty for this specific view
                actions={reconciliationActions}
              />
              <TanStackDynamicTable
                data={coverSheetData}
                columns={coverSheetColumns}
                rowCount={coverSheetData.length}
                tableName="coversheet_view"
                onStateChange={() => {}}
                showPagination={false}
                actions={coverSheetActions}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 italic">
            Select a batch from the sidebar to view reconciliation data.
          </div>
        )}
        <NotesPane
          isOpen={isNotesOpen}
          onClose={() => { setIsNotesOpen(false); setNotesPinned(false); setNotesBatchId(null); }}
          notes={notes}
          loading={notesLoading}
          currentUserName={accounts[0]?.name}
          // Create
          newNoteText={newNoteText}
          setNewNoteText={setNewNoteText}
          onCreateNote={handleCreateNote}
          // Update
          editingNoteId={editingNoteId}
          setEditingNoteId={setEditingNoteId}
          editingNoteText={editingNoteText}
          setEditingNoteText={setEditingNoteText}
          onUpdateNote={handleUpdateNote}
          // Delete
          onDeleteNote={handleDeleteNote}
        />
      </main>
      </div>

      {/* Change Date Modal */}
      {showDateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 w-80">
            <h2 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-4">
              Change Batch Date
            </h2>
            <ProDatePicker value={newDate} onChange={(val) => setNewDate(val)} />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setShowDateModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleDateChange}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      <BatchPresetModal
        isOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        onPresetsChanged={(next) => {
          setPresets(next);
          // If the selected preset was deleted, clear the selection.
          if (selectedPresetId && !next.some((p) => p.id === selectedPresetId)) {
            setSelectedPresetId(null);
          }
        }}
      />
    </div>
  );
};

export default BatchPage;
